import React, { useState } from 'react';
import { Button, Modal, ButtonGroup, Grid, Text, Stack, AnchorTextLink } from '@chaser/components';
import QuickBooksButton from '../../QuickbooksButton/index.jsx';
import ConnectService from '../../../services/connect-service';
import AccountsIQModal from '../../../app/connect/components/AccountsIQModal/index';
import OpenAPIModal from '../../../app/connect/components/OpenAPIModal/index.jsx';
import Sage200Modal from '../../../app/connect/components/Sage200Modal/index.jsx';
import SageIntacctModal from '../../../app/connect/components/SageIntacctModal/index.jsx';
import useModal from '../../../hooks/useModal';
import { useHistory } from 'react-router';
import { hasErrorCode } from '../../../util/errorUtils';
import { IntegrationDisplayNames, Integrations, DemoIntegrations } from '../../../util/constants';
import IntegrationList from '../../../app/connect/components/IntegrationList';
import usePush from '../../../hooks/usePush';
import cx from 'classnames';
import style from '../../../app/connect/components/IntegrationList/Integrations.module.scss';
import OdooModal from '../../../app/connect/components/OdooModal/index.jsx';
import StripeModal from '../../../app/connect/components/StripeModal/index.jsx';

function AddOrganisationModal({ close, open: orgModalOpen, onClosed }) {
  const history = useHistory();
  const showModal = useModal({ cleanupOnUnmount: false });
  const [accountingSoftware, setAccountingSoftware] = useState(null);
  const isQBO = accountingSoftware === Integrations.QUICKBOOKS;

  const { execute, loading } = usePush(ConnectService.connect, {
    errorToast: 'Failed to connect',
  });

  async function handleSubmit(e) {
    e.preventDefault();
    close();

    switch (accountingSoftware) {
      case Integrations.ACCOUNTSIQ:
        showModal(<AccountsIQModal />);
        break;
      case Integrations.CSV:
        history.push('/addorganisation/csvconfiguration');
        break;
      case Integrations.OPENAPI:
        showModal(<OpenAPIModal integrationType={accountingSoftware} />);
        break;
      case Integrations.ZAPIER:
        showModal(<OpenAPIModal integrationType={accountingSoftware} />);
        break;
      case Integrations.SAGE50:
        showModal(<OpenAPIModal integrationType={accountingSoftware} />);
        break;
      case Integrations.SAGE200:
        showModal(<Sage200Modal />);
        break;
      case DemoIntegrations.OPENAPI:
        showModal(<OpenAPIModal integrationType={Integrations.OPENAPI} sandbox />);
        break;
      case Integrations.SAGEINTACCT:
        showModal(<SageIntacctModal />);
        break;
      case Integrations.ODOO:
        showModal(<OdooModal />);
        break;
      case Integrations.STRIPE:
        showModal(<StripeModal />);
        break;
      default: {
        try {
          const [err, url] = await execute(accountingSoftware);
          if (err) return;
          window.location = url;
        } catch (e) {}
      }
    }
  }

  let buttonLabel = 'Connect';
  if (accountingSoftware) {
    buttonLabel =
      accountingSoftware === DemoIntegrations.OPENAPI
        ? 'Continue with Demo Organisation'
        : `Connect with ${IntegrationDisplayNames[accountingSoftware] || accountingSoftware}`;
  }

  return (
    <>
      <Modal
        size="large"
        title="Add a new organisation"
        open={orgModalOpen}
        toggle={close}
        onClosed={onClosed}
      >
        <form onSubmit={handleSubmit} action="#">
          <Modal.Body>
            <Stack gap="large">
              <IntegrationList
                onIntegrationSelected={setAccountingSoftware}
                selectedIntegration={accountingSoftware}
              />
              <hr />
              <Text>Curious to see how it works?</Text>
              <Text color="Mid Neutral 1">
                Try Chaser with a demo organization pre-filled with sample data. Experience all the
                features without sharing your data or connecting your system. Send a test chase to
                yourself and see Chaser in action!
              </Text>
              <Grid width="100%">
                <Button
                  type="button"
                  intent="chaser"
                  appearance="secondary"
                  data-testid={`${DemoIntegrations.OPENAPI}-add`}
                  data-trackingid={`${DemoIntegrations.OPENAPI}-add`}
                  value={DemoIntegrations.OPENAPI}
                  className={cx(
                    style.button,
                    accountingSoftware === DemoIntegrations.OPENAPI && style.active,
                  )}
                  onClick={() => setAccountingSoftware(DemoIntegrations.OPENAPI)}
                >
                  <Text fontWeight="bold" fontSize="medium" color="orangeAA">
                    Demo Organisation
                  </Text>
                </Button>
              </Grid>

              <Text as="p" color="Dark Neutral 1">
                For more information on how we handle your data, please see our{' '}
                <AnchorTextLink href="https://chaserhq.com/privacy-policy">
                  privacy policy
                </AnchorTextLink>
                .
              </Text>
            </Stack>
          </Modal.Body>
          <Modal.Footer>
            <ButtonGroup>
              <Button onClick={() => close()} appearance="secondary">
                Cancel
              </Button>
              {isQBO ? (
                <QuickBooksButton type="submit" />
              ) : (
                <Button
                  loading={loading}
                  type="submit"
                  data-testid="connect-btn"
                  data-trackingid="connect-btn"
                  id="submit-addclient-btn"
                  disabled={!accountingSoftware}
                >
                  {buttonLabel}
                </Button>
              )}
            </ButtonGroup>
          </Modal.Footer>
        </form>
      </Modal>
    </>
  );
}

export default AddOrganisationModal;
